import dayjs from "dayjs";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import {
    currentPeriodDays,
    daysForNextPeriod,
    daysForPreviousPeriod,
    getNumberOfDaysInPeriod,
    getWeekDays,
    getWeekNumbers,
    isCurrentWeekAndYear,
    isPastWeek,
    periodRows
} from "../index";

import { DayAsTile } from "../Day";
import { useCalendarShortcuts } from "../shortcuts";

import "./Calendar.scss";

function Calendar({ initialDate, period, setSelectedDate, layoutOnClick, onMonthClick }) {
    const table = useSelector(state => state.settings.theme) === 'table';
    const Skeuomorphism = useSelector(state => state.settings.design) === 'Skeuomorphism';
    const isPeriodQuarterYear = period === 3
    const weekdays = (table && !isPeriodQuarterYear && !Skeuomorphism)
        ? ['ПОНЕД.', 'ВТОРНИК', 'СРЕДА', 'ЧЕТВЕРГ', 'ПЯТНИЦА', 'СУББОТА', 'ВОСКРЕС.']
        : ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];
    const themeColor = useSelector(state => state.settings.themeColor);

    // The grid depends only on the start date and period, and rebuilding it is most of a render.
    const { days, periods } = useMemo(() => {
        if (period > 1) return periodRows(initialDate, period);

        const monthDaysCount = getNumberOfDaysInPeriod(initialDate.date(1), 1);
        const currentMonthDays = currentPeriodDays(monthDaysCount, initialDate.date(1));
        const previousMonthDays = daysForPreviousPeriod(initialDate, currentMonthDays);
        const nextMonthDays = daysForNextPeriod(initialDate, currentMonthDays, period);
        const days = [...previousMonthDays, ...currentMonthDays, ...nextMonthDays];
        const weekdays = getWeekDays(days);

        // The month view has no month headings.
        return { days, periods: [{ weekdays, weekNumbers: getWeekNumbers(weekdays), monthsLength: [] }] };
    }, [initialDate, period]);

    // Set when the highlighted thematic started before the first visible day.
    const outsideThematic = useSelector(state => {
        const highlighted = state.thematics.highlightedThematics;
        return highlighted != null && days[0].date.isAfter(highlighted.date_from) ? highlighted : null;
    });

    const alternate = useSelector(state => state.settings.theme) === 'rounded' && period === 1;
    const showPastDays = useSelector(state => state.settings.showPastDays);
    useCalendarShortcuts();

    return (<>
        {outsideThematic &&
            <div className="thematic-outside" >
                <span dangerouslySetInnerHTML={{ __html: `Тематика: ${outsideThematic.name}` }}>
                </span>
                <br />

                <span dangerouslySetInnerHTML={{ __html: `Старт:${dayjs(outsideThematic.date_from).format('DD-MM-YYYY')}` }}>
                </span>

            </div>
        }
        {
            Skeuomorphism && period === 1 &&
            <div
                className={classNames({
                    'month-number': true,

                })}
            >
                <span className="month-and-week-number--span">
                    {dayjs(initialDate).locale('ru').format('MMMM').toUpperCase()}
                </span>

                <span className="month-and-week-number--span">
                    {dayjs(initialDate).year()}
                </span>
            </div >
        }

        {periods.map((p, i) => {
            return (
                <table
                    key={i}
                    className={classNames({
                        'table-view': table,
                        'alternate-view': alternate,
                        'modern-view': !table && !alternate,
                        'period-is-month': period === 1,
                        'period-is-half-year': period === 6,
                        'period-is-year': period === 12,
                        'period-is-quarter-year': period === 3,

                    })}>

                    <thead>
                        {period > 1 &&
                            (<tr>
                                <td className={classNames({
                                    'period-is-quarter-year-weekday': isPeriodQuarterYear,
                                    'gray': true,
                                    'max-w-40px': true

                                })}>
                                </td>
                                {p.monthsLength.map((month, index) => {
                                    return (
                                        <td key={index} onClick={() => { onMonthClick(2, month.monthNum, month.year) }}
                                            className="month"
                                            colSpan={month.monthLength}>
                                            <span>{month.date()}</span></td>
                                    )
                                })}
                            </tr>)
                        }
                        <tr style={{ position: 'relative' }}>
                            <td className={classNames({
                                'period-is-quarter-year-weekday': isPeriodQuarterYear,
                                'gray': Skeuomorphism,
                                'shifted-to-top': Skeuomorphism && period !== 1,
                                'w-40px': Skeuomorphism && period !== 1,
                                'w-full': Skeuomorphism && period === 1,
                                'max-w-40px': Skeuomorphism

                            })}>&nbsp;</td>
                            {p.weekNumbers.map((weekNumber, index) => {
                                return <td className={classNames({
                                    'day-of-week-month': true,
                                    'day-of-month--past-week': isPastWeek(weekNumber.date) && showPastDays,
                                    'period-is-month': period === 1,
                                    'period-is-quarter-year': period === 3,
                                    'period-is-third-of-year': period === 4,
                                    'period-is-half-year': period === 6,
                                    'period-is-year': period === 12,
                                    'day-of-month--current-week': isCurrentWeekAndYear(weekNumber.date),
                                    'alternate-weeknumber': alternate,
                                    'table-weeknumber': table,
                                    'blue': themeColor === 'blue' && !alternate,
                                })}
                                    key={index}
                                    onClick={() => layoutOnClick(dayjs(weekNumber.date).week(weekNumber.weekNumber), 1)}>{weekNumber.weekNumber}</td>
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(p.weekdays).map(([weekDay, days], index) => {
                            return (
                                <tr key={index}>
                                    <td className={classNames(
                                        {
                                            'day-of-week-month': true,
                                            'day-of-month': period < 3,
                                            'period-is-third-of-year-day': period === 4,
                                            'period-is-quarter-year-day': period === 3,
                                            'period-is-half-year-day': period === 6,
                                            'period-is-year-day': period === 12,
                                            'weekdays': true,
                                            'weekend': +weekDay === 5 || +weekDay === 6,
                                            'max-w-40px': Skeuomorphism,
                                        }
                                    )}>{weekdays[weekDay]}</td>
                                    {/* Keyed by day, so that a step of a week redraws only the days that change place. */}
                                    {days.map((day) => <DayAsTile key={day.date.valueOf()}
                                        period={period} day={day}
                                        alternate={alternate}
                                        table={table}
                                    />)}

                                </tr>
                            )
                        }
                        )}
                    </tbody>
                </table>
            )
        })}</>)
}

export default Calendar;